import { RiAddLine, RiDeleteBinLine, RiImageLine, RiLoader4Line, RiEditLine, RiCloseLine } from 'react-icons/ri';
import { API_BASE_URL } from '../../config';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const ManageNews = () => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        tag: 'New',
        image_url: '',
        content: ''
    });
    const [customTag, setCustomTag] = useState('');
    const [isCustom, setIsCustom] = useState(false);

    useEffect(() => {
        fetchNews();
    }, []);

    const fetchNews = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/news/read.php`);
            const data = await response.json();
            if (response.ok) {
                setNews(data.records || []);
            }
        } catch (error) {
            console.error('Error fetching news:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const url = editingId
            ? `${API_BASE_URL}/news/update.php`
            : `${API_BASE_URL}/news/create.php`;

        const finalPayload = {
            ...formData,
            tag: isCustom ? customTag : formData.tag,
            id: editingId
        };

        const payload = editingId ? finalPayload : { ...finalPayload, id: undefined };

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setFormData({ title: '', tag: 'New', image_url: '', content: '' });
                setCustomTag('');
                setIsCustom(false);
                setEditingId(null);
                fetchNews();
            } else {
                alert(`Failed to ${editingId ? 'update' : 'create'} news item`);
            }
        } catch (error) {
            console.error(`Error ${editingId ? 'updating' : 'creating'} news:`, error);
        }
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        const standardTags = ['New', 'Latest', 'Insight', 'Discovery', 'Conservation', 'Policy', 'Research'];
        const isStandard = standardTags.includes(item.tag);

        setIsCustom(!isStandard);
        setCustomTag(isStandard ? '' : item.tag);

        setFormData({
            title: item.title,
            tag: isStandard ? item.tag : 'Other',
            image_url: item.image_url || '',
            content: item.content
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setIsCustom(false);
        setCustomTag('');
        setFormData({ title: '', tag: 'New', image_url: '', content: '' });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/news/delete.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id })
            });

            if (response.ok) {
                fetchNews();
            }
        } catch (error) {
            console.error('Error deleting news:', error);
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h2 className="text-3xl font-bold text-slate-800">Manage News</h2>
                    <p className="text-slate-500 text-sm mt-1">Create and manage news articles</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Form Column */}
                <div className="lg:col-span-1">
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 sticky top-8">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-bold text-lg flex items-center gap-2">
                                {editingId ? (
                                    <>
                                        <RiEditLine className="text-amber-500" />
                                        Update Article
                                    </>
                                ) : (
                                    <>
                                        <RiAddLine className="text-emerald-500" />
                                        Add New Article
                                    </>
                                )}
                            </h3>
                            {editingId && (
                                <button
                                    onClick={cancelEdit}
                                    className="text-slate-400 hover:text-slate-600 transition-colors"
                                    title="Cancel Editing"
                                >
                                    <RiCloseLine size={20} />
                                </button>
                            )}
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className="w-full p-3 bg-slate-50 rounded-lg border-none text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1">Tag</label>
                                <select
                                    value={isCustom ? 'Other' : formData.tag}
                                    onChange={(e) => {
                                        if (e.target.value === 'Other') {
                                            setIsCustom(true);
                                        } else {
                                            setIsCustom(false);
                                            setFormData({ ...formData, tag: e.target.value });
                                        }
                                    }}
                                    className="w-full p-3 bg-slate-50 rounded-lg border-none text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none"
                                >
                                    <option value="New">New</option>
                                    <option value="Latest">Latest</option>
                                    <option value="Insight">Insight</option>
                                    <option value="Discovery">Discovery</option>
                                    <option value="Conservation">Conservation</option>
                                    <option value="Policy">Policy</option>
                                    <option value="Research">Research</option>
                                    <option value="Other">Other (Custom Tag)...</option>
                                </select>
                            </div>
                            {isCustom && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-2"
                                >
                                    <input
                                        type="text"
                                        placeholder="Type new tag name..."
                                        value={customTag}
                                        onChange={(e) => setCustomTag(e.target.value)}
                                        className="w-full p-3 bg-slate-50 rounded-lg border-2 border-emerald-100 text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none"
                                    />
                                </motion.div>
                            )}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1">Image URL</label>
                                <input
                                    type="url"
                                    value={formData.image_url}
                                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                                    className="w-full p-3 bg-slate-50 rounded-lg border-none text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none"
                                    placeholder="https://..."
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1">Content</label>
                                <textarea
                                    value={formData.content}
                                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                    className="w-full p-3 bg-slate-50 rounded-lg border-none text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none h-32 resize-none"
                                    required
                                />
                            </div>
                            <button className={`w-full font-bold py-3 rounded-xl transition-colors ${editingId
                                ? 'bg-amber-500 hover:bg-amber-600 text-white'
                                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                }`}>
                                {editingId ? 'Update Article' : 'Publish Article'}
                            </button>
                        </form>
                    </div>
                </div>

                {/* List Column */}
                <div className="lg:col-span-2">
                    {loading ? (
                        <div className="flex justify-center py-20 text-emerald-500">
                            <RiLoader4Line className="animate-spin text-4xl" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {news.map((item) => (
                                <div key={item.id} className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex gap-4 group">
                                    <div className="w-32 h-24 bg-slate-100 rounded-lg flex-shrink-0 overflow-hidden relative">
                                        {item.image_url ? (
                                            <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                <RiImageLine size={24} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-start">
                                            <span className="text-[10px] uppercase font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded mb-2 inline-block">
                                                {item.tag}
                                            </span>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleEdit(item)}
                                                    className="text-slate-300 hover:text-emerald-500 transition-colors p-1"
                                                    title="Edit Article"
                                                >
                                                    <RiEditLine size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                                    title="Delete Article"
                                                >
                                                    <RiDeleteBinLine size={18} />
                                                </button>
                                            </div>
                                        </div>
                                        <h4 className="font-bold text-slate-800 text-lg leading-tight mb-2">{item.title}</h4>
                                        <p className="text-slate-500 text-sm line-clamp-2">{item.content}</p>
                                    </div>
                                </div>
                            ))}

                            {news.length === 0 && (
                                <div className="text-center py-20 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                    <p className="text-slate-400 font-medium">No published news yet</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManageNews;
